import { Injectable } from '@angular/core';
import {
  FlattenedCollectionPoint,
  FlattenedSociety,
  FlattenedUnitOffice,
  FlattenedBCC
} from '../views/login/hierarchy.model';
import { DataService } from './data.service';

@Injectable({
  providedIn: 'root'
})
export class HierarchyService {

  constructor(private dataService: DataService) { }

  // ── Access Level Constants ─────────────────────────────────────────────────
  readonly ACCESS_DAIRY        = 'Dairy Level';
  readonly ACCESS_UNIT_OFFICE  = 'Unit Office Level';
  readonly ACCESS_BCC          = 'BCC Level';
  readonly ACCESS_SUPERVISOR   = 'Supervisor';
  readonly ACCESS_SOCIETY      = 'Society Level';
  readonly ACCESS_COLLECTION   = 'Collection Point Level';

  // ══════════════════════════════════════════════════════════════════════════
  // ROLE-BASED VISIBILITY HELPERS
  // These are used in report components to show/hide dropdowns
  // ══════════════════════════════════════════════════════════════════════════

  /**
   * Should the Unit Office dropdown be visible?
   * Only Dairy Level and Supervisor see this dropdown.
   * UO/BCC/Society/CP users have it pre-fixed — no need to show.
   */
  showUnitOfficeDropdown(): boolean {
    const level = this.getUserAccessLevel();
    return [this.ACCESS_DAIRY, this.ACCESS_SUPERVISOR].includes(level);
  }

  /**
   * Should the BCC dropdown be visible?
   * Dairy, Supervisor, and Unit Office Level users see this.
   */
  showBCCDropdown(): boolean {
    const level = this.getUserAccessLevel();
    return [this.ACCESS_DAIRY, this.ACCESS_SUPERVISOR, this.ACCESS_UNIT_OFFICE].includes(level);
  }

  /**
   * Should the Collection Point dropdown be visible?
   * All supervisor-and-above roles see this. Society/CP users are pre-fixed.
   */
  showCollectionPointDropdown(): boolean {
    const level = this.getUserAccessLevel();
    return [
      this.ACCESS_DAIRY,
      this.ACCESS_SUPERVISOR,
      this.ACCESS_UNIT_OFFICE,
      this.ACCESS_BCC
    ].includes(level);
  }

  /**
   * Is the user a supervisor or higher level (not society/CP)?
   */
  isSupervisorOrAbove(): boolean {
    const level = this.getUserAccessLevel();
    return [
      this.ACCESS_SUPERVISOR,
      this.ACCESS_BCC,
      this.ACCESS_UNIT_OFFICE,
      this.ACCESS_DAIRY
    ].includes(level);
  }

  /**
   * Is the user at society or collection point level (lowest levels)?
   */
  isSocietyOrBelow(): boolean {
    const level = this.getUserAccessLevel();
    return [this.ACCESS_SOCIETY, this.ACCESS_COLLECTION].includes(level);
  }

  // ══════════════════════════════════════════════════════════════════════════
  // SESSION / FETCH
  // ══════════════════════════════════════════════════════════════════════════

  private getEmpId(): number {
    const empId = sessionStorage.getItem('empId');
    return empId ? parseInt(empId, 10) : 0;
  }

  /**
   * Fetch hierarchy from API and store it.
   * Called from login component after successful login.
   */
  fetchHierarchyData(): Promise<any> {
    const empId = this.getEmpId();
    if (!empId) {
      return Promise.reject('No empId found in session');
    }

    return new Promise((resolve, reject) => {
      this.dataService.getHierarchy(empId).subscribe(
        (response: any) => {
          if (response && response.status === 200 && response.data) {
            this.storeHierarchyData(response.data);
            resolve(response.data);
          } else {
            reject('No hierarchy data received');
          }
        },
        (error) => {
          console.error('Error fetching hierarchy:', error);
          reject(error);
        }
      );
    });
  }

  // ══════════════════════════════════════════════════════════════════════════
  // STORAGE
  // ══════════════════════════════════════════════════════════════════════════

  storeHierarchyData(data: any[]): void {
    localStorage.setItem('userHierarchy', JSON.stringify(data));

    if (data[0]?.accessLevel) {
      localStorage.setItem('userAccessLevel', data[0].accessLevel);
    }

    if (data[0]?.dairy) {
      localStorage.setItem('userDairyInfo', JSON.stringify(data[0].dairy));
    }

    this.processAndStoreFlattenedHierarchy(data);
  }

  processAndStoreFlattenedHierarchy(hierarchyData: any[]): void {
    if (!hierarchyData || !hierarchyData[0]) return;

    const userData    = hierarchyData[0];
    const accessLevel = userData.accessLevel;
    const dairy       = userData.dairy;

    if (!dairy) return;

    const flattenedCollectionPoints: any[] = [];
    const flattenedSocieties:        any[] = [];
    const flattenedUnitOffices:      any[] = [];
    const flattenedBCCs:             any[] = [];
    const flattenedChillerUnits:     any[] = [];

    if (dairy.unitOffices && dairy.unitOffices.length > 0) {
      dairy.unitOffices.forEach((unitOffice: any) => {

        flattenedUnitOffices.push({
          unitOfficeId:   unitOffice.unitOfficeId,
          unitOfficeName: unitOffice.unitOfficeName,
          dairyId:        dairy.dairyId,
          dairyName:      dairy.dairyName,
          bccs:           unitOffice.bccs || []
        });

        if (unitOffice.bccs && unitOffice.bccs.length > 0) {
          unitOffice.bccs.forEach((bcc: any) => {

            flattenedBCCs.push({
              bccId:          bcc.bccId,
              bccName:        bcc.bccName,
              unitOfficeId:   unitOffice.unitOfficeId,
              unitOfficeName: unitOffice.unitOfficeName,
              dairyId:        dairy.dairyId,
              dairyName:      dairy.dairyName,
              chillerUnits:   bcc.chillerUnits || []
            });

            if (bcc.chillerUnits && bcc.chillerUnits.length > 0) {
              bcc.chillerUnits.forEach((chiller: any) => {

                flattenedChillerUnits.push({
                  chillerUnitId:   chiller.chillerUnitId,
                  chillerUnitName: chiller.chillerUnitName,
                  bccId:           bcc.bccId,
                  bccName:         bcc.bccName,
                  unitOfficeId:    unitOffice.unitOfficeId,
                  unitOfficeName:  unitOffice.unitOfficeName,
                  dairyId:         dairy.dairyId,
                  dairyName:       dairy.dairyName,
                  societies:       chiller.societies || []
                });

                if (chiller.societies && chiller.societies.length > 0) {
                  chiller.societies.forEach((society: any) => {

                    flattenedSocieties.push({
                      societyId:        society.societyId,
                      societyName:      society.societyName,
                      chillerUnitId:    chiller.chillerUnitId,
                      chillerUnitName:  chiller.chillerUnitName,
                      bccId:            bcc.bccId,
                      bccName:          bcc.bccName,
                      unitOfficeId:     unitOffice.unitOfficeId,
                      unitOfficeName:   unitOffice.unitOfficeName,
                      dairyId:          dairy.dairyId,
                      dairyName:        dairy.dairyName,
                      collectionPoints: society.collectionPoints || []
                    });

                    if (society.collectionPoints && society.collectionPoints.length > 0) {
                      society.collectionPoints.forEach((cp: any) => {
                        flattenedCollectionPoints.push({
                          collectionPointId:   cp.collectionPointId,
                          collectionPointName: cp.collectionPointName,
                          societyId:           society.societyId,
                          societyName:         society.societyName,
                          chillerUnitId:       chiller.chillerUnitId,
                          chillerUnitName:     chiller.chillerUnitName,
                          bccId:               bcc.bccId,
                          bccName:             bcc.bccName,
                          unitOfficeId:        unitOffice.unitOfficeId,
                          unitOfficeName:      unitOffice.unitOfficeName,
                          dairyId:             dairy.dairyId,
                          dairyName:           dairy.dairyName,
                          stateId:             cp.state?.stateId,
                          stateName:           cp.state?.stateName,
                          districtId:          cp.district?.districtId,
                          districtName:        cp.district?.districtName,
                          villageId:           cp.village?.villageId,
                          villageName:         cp.village?.villageName,
                          fullPath:            `${dairy.dairyName} > ${unitOffice.unitOfficeName} > ${bcc.bccName} > ${chiller.chillerUnitName} > ${society.societyName} > ${cp.collectionPointName}`
                        });
                      });
                    }
                  });
                }
              });
            }
          });
        }
      });
    }

    // Fallback for Collection Point Level users where hierarchy may be shallow
    if (accessLevel === this.ACCESS_COLLECTION && flattenedCollectionPoints.length === 0) {
      try {
        const cp      = dairy.unitOffices?.[0]?.bccs?.[0]?.chillerUnits?.[0]?.societies?.[0]?.collectionPoints?.[0];
        const society = dairy.unitOffices?.[0]?.bccs?.[0]?.chillerUnits?.[0]?.societies?.[0];
        const chiller = dairy.unitOffices?.[0]?.bccs?.[0]?.chillerUnits?.[0];
        const bcc     = dairy.unitOffices?.[0]?.bccs?.[0];
        const uo      = dairy.unitOffices?.[0];

        if (cp) {
          flattenedCollectionPoints.push({
            collectionPointId:   cp.collectionPointId,
            collectionPointName: cp.collectionPointName,
            societyId:           society.societyId,
            societyName:         society.societyName,
            chillerUnitId:       chiller.chillerUnitId,
            chillerUnitName:     chiller.chillerUnitName,
            bccId:               bcc.bccId,
            bccName:             bcc.bccName,
            unitOfficeId:        uo.unitOfficeId,
            unitOfficeName:      uo.unitOfficeName,
            dairyId:             dairy.dairyId,
            dairyName:           dairy.dairyName,
            stateId:             cp.state?.stateId,
            stateName:           cp.state?.stateName,
            districtId:          cp.district?.districtId,
            districtName:        cp.district?.districtName,
            villageId:           cp.village?.villageId,
            villageName:         cp.village?.villageName,
            fullPath:            `${dairy.dairyName} > ${uo.unitOfficeName} > ${bcc.bccName} > ${chiller.chillerUnitName} > ${society.societyName} > ${cp.collectionPointName}`
          });
        }
      } catch (error) {
        console.error('Error in CP-level fallback extraction:', error);
      }
    }

    localStorage.setItem('flattenedCollectionPoints', JSON.stringify(flattenedCollectionPoints));
    localStorage.setItem('flattenedSocieties',        JSON.stringify(flattenedSocieties));
    localStorage.setItem('flattenedUnitOffices',      JSON.stringify(flattenedUnitOffices));
    localStorage.setItem('flattenedBCCs',             JSON.stringify(flattenedBCCs));
    localStorage.setItem('flattenedChillerUnits',     JSON.stringify(flattenedChillerUnits));

    console.log('Hierarchy processed:', {
      accessLevel,
      unitOffices:      flattenedUnitOffices.length,
      bccs:             flattenedBCCs.length,
      chillerUnits:     flattenedChillerUnits.length,
      societies:        flattenedSocieties.length,
      collectionPoints: flattenedCollectionPoints.length
    });
  }

  // ══════════════════════════════════════════════════════════════════════════
  // CASCADE FILTER METHODS — used by report components for dropdowns
  // ══════════════════════════════════════════════════════════════════════════

  /**
   * STEP 1 — Get Unit Offices for dropdown.
   *
   * - Dairy Level / Supervisor → all unit offices
   * - Unit Office Level        → only their single unit office (dropdown hidden in UI anyway)
   * - BCC / Society / CP       → empty (they never need this)
   */
  getUnitOfficesForFilter(): FlattenedUnitOffice[] {
    const level = this.getUserAccessLevel();

    if (level === this.ACCESS_DAIRY || level === this.ACCESS_SUPERVISOR) {
      // Full list — they can pick any unit office
      return this.getFlattenedUnitOffices();
    }

    if (level === this.ACCESS_UNIT_OFFICE) {
      // Return only their own UO (used to pre-populate, not shown as dropdown)
      return this.getFlattenedUnitOffices();
    }

    // BCC / Society / CP — return empty; UO is not selectable for them
    return [];
  }

  /**
   * STEP 2 — Get BCCs filtered by selected unitOfficeId.
   *
   * - Dairy / Supervisor       → BCCs under the chosen unit office (or all if none selected)
   * - Unit Office Level        → BCCs under their fixed unit office
   * - BCC Level                → only their own BCC (used to pre-populate)
   * - Society / CP             → empty
   *
   * @param unitOfficeId  Pass the selected UO id, or null for no filter
   */
  getBCCsForFilter(unitOfficeId: number | null): FlattenedBCC[] {
    const level  = this.getUserAccessLevel();
    const allBCCs = this.getFlattenedBCCs();

    if (level === this.ACCESS_DAIRY || level === this.ACCESS_SUPERVISOR) {
      return unitOfficeId != null
        ? allBCCs.filter(b => b.unitOfficeId === unitOfficeId)
        : allBCCs;
    }

    if (level === this.ACCESS_UNIT_OFFICE) {
      // Their BCCs are already scoped to their UO from hierarchy
      return unitOfficeId != null
        ? allBCCs.filter(b => b.unitOfficeId === unitOfficeId)
        : allBCCs;
    }

    if (level === this.ACCESS_BCC) {
      // Return their own BCCs — used to auto-select, not shown as dropdown
      return allBCCs;
    }

    return [];
  }

  /**
   * STEP 3 — Get Collection Points filtered by bccId and/or unitOfficeId.
   *
   * - Dairy / Supervisor / UO / BCC → filtered CPs
   * - Society / CP                  → their single CP is auto-selected; not a dropdown
   *
   * @param bccId         Selected BCC id, or null
   * @param unitOfficeId  Selected Unit Office id, or null
   */
  getCollectionPointsForFilter(
    bccId: number | null,
    unitOfficeId: number | null = null
  ): FlattenedCollectionPoint[] {
    const level = this.getUserAccessLevel();

    // Society/CP level — return their fixed CP(s) but no dropdown shown
    if (level === this.ACCESS_SOCIETY || level === this.ACCESS_COLLECTION) {
      return this.getFlattenedCollectionPoints();
    }

    let cps = this.getFlattenedCollectionPoints();

    if (unitOfficeId != null) {
      cps = cps.filter(cp => cp.unitOfficeId === unitOfficeId);
    }
    if (bccId != null) {
      cps = cps.filter(cp => cp.bccId === bccId);
    }

    return cps;
  }

  /**
   * Build a complete payload context object based on the user's role and
   * whatever selections have been made in the dropdowns.
   *
   * Report components can call this instead of manually assembling
   * dairyId / unitOfficeId / bccId / collectionPointId every time.
   *
   * @param selectedUnitOfficeId   null if user hasn't selected / doesn't see the dropdown
   * @param selectedBccId          null if user hasn't selected / doesn't see the dropdown
   * @param selectedCpId           null if user hasn't selected / doesn't see the dropdown
   */
  buildFilterPayload(
    selectedUnitOfficeId: number | null = null,
    selectedBccId: number | null = null,
    selectedCpId: number | null = null
  ): any {
    const level = this.getUserAccessLevel();
    const dairy = this.getDairyInfo();

    // ── Dairy / Supervisor — all three dropdowns selectable ────────────────
    if (level === this.ACCESS_DAIRY || level === this.ACCESS_SUPERVISOR) {
      const cp = selectedCpId
        ? this.getCollectionPointById(selectedCpId)
        : this.getDefaultCollectionPoint(selectedBccId, selectedUnitOfficeId);

      return {
        dairyId:           cp?.dairyId        ?? dairy?.dairyId,
        unitOfficeId:      cp?.unitOfficeId    ?? selectedUnitOfficeId,
        bccId:             cp?.bccId           ?? selectedBccId,
        collectionPointId: cp?.collectionPointId ?? null
      };
    }

    // ── Unit Office Level — UO is fixed, BCC + CP selectable ──────────────
    if (level === this.ACCESS_UNIT_OFFICE) {
      const uo = this.getFlattenedUnitOffices()[0];
      const cp = selectedCpId
        ? this.getCollectionPointById(selectedCpId)
        : this.getDefaultCollectionPoint(selectedBccId, uo?.unitOfficeId ?? null);

      return {
        dairyId:           uo?.dairyId         ?? dairy?.dairyId,
        unitOfficeId:      uo?.unitOfficeId,
        bccId:             cp?.bccId           ?? selectedBccId,
        collectionPointId: cp?.collectionPointId ?? null
      };
    }

    // ── BCC Level — UO + BCC are fixed, CP selectable ─────────────────────
    if (level === this.ACCESS_BCC) {
      const bcc = this.getFlattenedBCCs()[0];
      const cp  = selectedCpId
        ? this.getCollectionPointById(selectedCpId)
        : this.getDefaultCollectionPoint(bcc?.bccId ?? null, null);

      return {
        dairyId:           bcc?.dairyId        ?? dairy?.dairyId,
        unitOfficeId:      bcc?.unitOfficeId,
        bccId:             bcc?.bccId,
        collectionPointId: cp?.collectionPointId ?? null
      };
    }

    // ── Society / CP Level — everything is fixed ──────────────────────────
    const cp = this.getFlattenedCollectionPoints()[0];
    return {
      dairyId:           cp?.dairyId         ?? dairy?.dairyId,
      unitOfficeId:      cp?.unitOfficeId,
      bccId:             cp?.bccId,
      collectionPointId: cp?.collectionPointId
    };
  }

  // ══════════════════════════════════════════════════════════════════════════
  // LOOKUP HELPERS
  // ══════════════════════════════════════════════════════════════════════════

  getDefaultCollectionPoint(
    bccId: number | null = null,
    unitOfficeId: number | null = null
  ): any {
    const cps = this.getCollectionPointsForFilter(bccId, unitOfficeId);
    return cps.length > 0 ? cps[0] : null;
  }

  getCollectionPointById(id: number): FlattenedCollectionPoint | null {
    return this.getFlattenedCollectionPoints().find(cp => cp.collectionPointId === id) ?? null;
  }

  getBCCById(id: number): FlattenedBCC | null {
    return this.getFlattenedBCCs().find(b => b.bccId === id) ?? null;
  }

  getUnitOfficeById(id: number): FlattenedUnitOffice | null {
    return this.getFlattenedUnitOffices().find(u => u.unitOfficeId === id) ?? null;
  }

  // ══════════════════════════════════════════════════════════════════════════
  // LOCALSTORAGE GETTERS
  // ══════════════════════════════════════════════════════════════════════════

  getHierarchyData(): any[] {
    const data = localStorage.getItem('userHierarchy');
    return data ? JSON.parse(data) : [];
  }

  getUserAccessLevel(): string {
    return localStorage.getItem('userAccessLevel') || '';
  }

  getDairyInfo(): any {
    const data = localStorage.getItem('userDairyInfo');
    return data ? JSON.parse(data) : null;
  }

  getFlattenedCollectionPoints(): FlattenedCollectionPoint[] {
    const data = localStorage.getItem('flattenedCollectionPoints');
    return data ? JSON.parse(data) : [];
  }

  getFlattenedSocieties(): FlattenedSociety[] {
    const data = localStorage.getItem('flattenedSocieties');
    return data ? JSON.parse(data) : [];
  }

  getFlattenedUnitOffices(): FlattenedUnitOffice[] {
    const data = localStorage.getItem('flattenedUnitOffices');
    return data ? JSON.parse(data) : [];
  }

  getFlattenedBCCs(): FlattenedBCC[] {
    const data = localStorage.getItem('flattenedBCCs');
    return data ? JSON.parse(data) : [];
  }

  getFlattenedChillerUnits(): any[] {
    const data = localStorage.getItem('flattenedChillerUnits');
    return data ? JSON.parse(data) : [];
  }

  // ══════════════════════════════════════════════════════════════════════════
  // FILTER BY PARENT — convenience methods used in some components
  // ══════════════════════════════════════════════════════════════════════════

  getCollectionPointsBySociety(societyId: number): FlattenedCollectionPoint[] {
    return this.getFlattenedCollectionPoints().filter(cp => cp.societyId === societyId);
  }

  getSocietiesByChillerUnit(chillerUnitId: number): FlattenedSociety[] {
    return this.getFlattenedSocieties().filter(s => s.chillerUnitId === chillerUnitId);
  }

  getSocietiesByBCC(bccId: number): FlattenedSociety[] {
    return this.getFlattenedSocieties().filter(s => s.bccId === bccId);
  }

  getSocietiesByUnitOffice(unitOfficeId: number): FlattenedSociety[] {
    return this.getFlattenedSocieties().filter(s => s.unitOfficeId === unitOfficeId);
  }

  getBCCsByUnitOffice(unitOfficeId: number): FlattenedBCC[] {
    return this.getFlattenedBCCs().filter(bcc => bcc.unitOfficeId === unitOfficeId);
  }

  getChillerUnitsByBCC(bccId: number): any[] {
    return this.getFlattenedChillerUnits().filter(cu => cu.bccId === bccId);
  }

  // ══════════════════════════════════════════════════════════════════════════
  // MISC
  // ══════════════════════════════════════════════════════════════════════════

  getCollectionPointPath(collectionPointId: number): string {
    const cp = this.getFlattenedCollectionPoints().find(c => c.collectionPointId === collectionPointId);
    return cp ? cp.fullPath : '';
  }

  hasAccessToCollectionPoint(collectionPointId: number): boolean {
    return this.getFlattenedCollectionPoints().some(cp => cp.collectionPointId === collectionPointId);
  }

  getAllUnitOffices(): FlattenedUnitOffice[]         { return this.getFlattenedUnitOffices(); }
  getAllBCCs(): FlattenedBCC[]                        { return this.getFlattenedBCCs(); }
  getAllChillerUnits(): any[]                         { return this.getFlattenedChillerUnits(); }
  getAllSocieties(): FlattenedSociety[]               { return this.getFlattenedSocieties(); }
  getAllCollectionPoints(): FlattenedCollectionPoint[] { return this.getFlattenedCollectionPoints(); }

  hasHierarchyData(): boolean {
    return localStorage.getItem('userHierarchy') !== null;
  }

  clearHierarchyData(): void {
    localStorage.removeItem('userHierarchy');
    localStorage.removeItem('userAccessLevel');
    localStorage.removeItem('userDairyInfo');
    localStorage.removeItem('flattenedCollectionPoints');
    localStorage.removeItem('flattenedSocieties');
    localStorage.removeItem('flattenedUnitOffices');
    localStorage.removeItem('flattenedBCCs');
    localStorage.removeItem('flattenedChillerUnits');
  }

  refreshHierarchyData(): Promise<any> {
    this.clearHierarchyData();
    return this.fetchHierarchyData();
  }

  debugHierarchy(): void {
    console.log('=== HIERARCHY DEBUG ===');
    console.log('Access Level:',         this.getUserAccessLevel());
    console.log('Show UO dropdown:',     this.showUnitOfficeDropdown());
    console.log('Show BCC dropdown:',    this.showBCCDropdown());
    console.log('Show CP dropdown:',     this.showCollectionPointDropdown());
    console.log('Unit Offices:',         this.getFlattenedUnitOffices());
    console.log('BCCs:',                 this.getFlattenedBCCs());
    console.log('Chiller Units:',        this.getFlattenedChillerUnits());
    console.log('Societies:',            this.getFlattenedSocieties());
    console.log('Collection Points:',    this.getFlattenedCollectionPoints());
    console.log('=== END DEBUG ===');
  }
}